import React, { useState } from 'react';
import { addTransactionApi } from '../../../Apis/Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

const AddTransaction = () => {
  // Categories including both income and expense
  const categories = [
    // Expense Categories
    'food', 
    'transportation',
    'utilities',
    'supplies',
    'rent',
    'maintenance',
    'salaries',
    'marketing',
    'taxes',
    'insurance',
    'professional_fees',
    'other_expenses',
    // Income Categories
    'sales',
    'services',
    'interest',
    'investments',
    'other_income'
  ];

  const paymentMethods = [
    'cash',
    'bank_transfer',
    'esewa',
    'fonepay',
    'khalti',
    'credit_card',
    'debit_card',
    'other_digital'
  ];

  const [transaction, setTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'expense',
    amount: '',
    category: '',
    paymentMethod: 'cash',
    description: '',
    remarks: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTransaction({
      ...transaction,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await addTransactionApi(transaction);
      if (response.data.success) {
        toast.success('Transaction added successfully!');
        // Reset form
        setTransaction({
          date: new Date().toISOString().split('T')[0],
          type: 'expense',
          amount: '',
          category: '',
          paymentMethod: 'cash',
          description: '',
          remarks: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDisplayName = (value) => {
    return value
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
  };

  return (
    <div className="mt-5">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="text-gray-600 font-bold text-3xl mb-6">Add New Transaction</div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date Field */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Date
              </label>
              <input
                type="date"
                name="date"
                value={transaction.date}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            {/* Transaction Type */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Transaction Type
              </label>
              <select
                name="type"
                value={transaction.type}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>

            {/* Amount Field */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Amount (NPR)
              </label>
              <input
                type="number"
                name="amount"
                value={transaction.amount}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter amount"
                min="0"
                step="0.01"
                required
              />
            </div>

            {/* Category Field */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Category
              </label>
              <select
                name="category"
                value={transaction.category}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {formatDisplayName(cat)}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Method Field */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Payment Method
              </label>
              <select
                name="paymentMethod"
                value={transaction.paymentMethod}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {formatDisplayName(method)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description Field */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={transaction.description}
              onChange={handleInputChange}
              className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter transaction description"
              rows="3"
              required
            />
          </div>

          {/* Remarks Field */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Remarks (Optional)
            </label>
            <input
              type="text"
              name="remarks"
              value={transaction.remarks}
              onChange={handleInputChange}
              className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Any additional notes"
            />
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-indigo-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-indigo-700 transition flex items-center ${
                isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                'Submitting...'
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
                  {transaction.type === 'income' ? 'Add Income' : 'Add Expense'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTransaction;